import React, { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { Order } from 'models/order';
import { orderWorkflowTemplate } from 'apollo/templates/workFlow/query.graphql';
import { useQuery } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { OrderStep, PauseLogEntry } from 'models/workflow-step';
import Loader from 'components/shared/loader/loader.component';
import StepLogRow from 'components/order-details/workflow-log/step-log-row.component';
import PauseLogRow from 'components/order-details/workflow-log/pause-log-row.component';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { permissions } from 'utils/permission';

type WorkflowLogProps = { order: Order };

const WorkflowLog: FC<WorkflowLogProps> = ({ order }) => {
    const { t: translate } = useTranslation(['workflow', 'common']);
    const { showBoundary } = useDenthubErrorBoundary();
    const currentUser = currentUserVar();
    const isExternalTechnician = permissions.isExternalTechnician(currentUser.role);

    const { data: { orderWorkflow } = {}, loading } = useQuery(orderWorkflowTemplate, {
        fetchPolicy: 'network-only',
        variables: { orderId: order._id },
        onError(error) {
            showBoundary(error);
        },
    });

    if (loading) {
        return <Loader />;
    }

    const visibleStepLogs = isExternalTechnician
        ? (orderWorkflow?.stepLogs || []).filter((step: OrderStep) =>
              step.externalTechnicians?.some((user) => user._id === currentUser._id),
          )
        : orderWorkflow?.stepLogs || [];

    return (
        <Fragment>
            {visibleStepLogs.length ? (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{translate('workflow:STEP')}</TableCell>
                                <TableCell>{translate('workflow:ENTERED_STEP')}</TableCell>
                                <TableCell>{translate('workflow:WORK_STARTED')}</TableCell>
                                <TableCell>{translate('common:TECHNICIAN')}</TableCell>
                                <TableCell>{translate('common:COMMENTS')}</TableCell>
                                <TableCell>{translate('workflow:EXTERNAL_USERS')}</TableCell>
                                <TableCell width={150}>{translate('workflow:TIME_IN_STEP')}</TableCell>
                                <TableCell width={150}>{translate('workflow:TIME_WAITING')}</TableCell>
                                <TableCell width={150}>{translate('workflow:TIME_PRODUCTION')}</TableCell>
                                <TableCell width={150}>{translate('workflow:PAUSE_TIME')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {visibleStepLogs.map((step: OrderStep) => (
                                <Fragment key={step._id ?? step.stepId}>
                                    <StepLogRow step={step} />
                                    {step.pauseLog?.map((entry: PauseLogEntry, index: number) => (
                                        <PauseLogRow
                                            key={`${step._id ?? step.stepId}-${entry.dateTime}`}
                                            entry={entry}
                                            pauseLog={step.pauseLog!}
                                            index={index}
                                        />
                                    ))}
                                </Fragment>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <Alert severity="info">{translate('workflow:NO_WORKFLOW_LOGS_FOUND')}</Alert>
            )}
        </Fragment>
    );
};

export default WorkflowLog;
